import React, { useState, useEffect } from 'react';
import {
    Search,
    Coins,
    ExternalLink,
    Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bondsAPI, formatCurrency, formatDate, formatCompactCurrency } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';

const FABondHoldings = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;

    // Debounce search term (to reduce API calls)
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch bonds data
    const { data: bondsResp, isPending: bondsLoading, error: bondsError } = useQuery({
        queryKey: ['fa-bonds-holdings', debouncedSearchTerm, statusFilter, typeFilter, currentPage],
        queryFn: () => bondsAPI.getBonds({
            search: debouncedSearchTerm || undefined,
            status: statusFilter === 'all' ? undefined : statusFilter,
            productType: typeFilter === 'all' ? undefined : typeFilter,
            page: currentPage,
            limit: PAGE_SIZE
        })
    });

    const respRoot = bondsResp?.data?.data ?? bondsResp?.data ?? bondsResp;
    const bonds = Array.isArray(respRoot?.bonds)
        ? respRoot.bonds
        : Array.isArray(respRoot?.data)
            ? respRoot.data
            : Array.isArray(respRoot?.items)
                ? respRoot.items
                : Array.isArray(respRoot?.docs)
                    ? respRoot.docs
                    : Array.isArray(respRoot?.results)
                        ? respRoot.results
                        : Array.isArray(respRoot)
                            ? respRoot
                            : [];
    const totalBonds = typeof respRoot?.total === 'number'
        ? respRoot.total
        : typeof respRoot?.count === 'number'
            ? respRoot.count
            : typeof respRoot?.pagination?.total === 'number'
                ? respRoot.pagination.total
                : bonds.length;
    const totalPages = Math.max(1, Math.ceil((totalBonds || bonds.length) / PAGE_SIZE));

    // Reset page when filters/search change
    useEffect(() => { setCurrentPage(1); }, [debouncedSearchTerm, statusFilter, typeFilter]);


    const getStatusBadge = (status) => {
        const statusClasses = {
            'Active': 'bg-green-100 text-green-800',
            'Listed': 'bg-green-100 text-green-800',
            'Pending Review': 'bg-yellow-100 text-yellow-800',
            'Approved': 'bg-green-100 text-green-800',
            'Rejected': 'bg-red-100 text-red-800',
            'Draft': 'bg-gray-100 text-gray-800',
            'Redemption in Process': 'bg-orange-100 text-orange-800',
            'Redemption Completed': 'bg-green-100 text-green-800',
            'Burn in Process': 'bg-red-100 text-red-800',
            'Burn Completed': 'bg-gray-100 text-gray-800',
            'Matured': 'bg-purple-100 text-purple-800',
            'Allocation Finalized': 'bg-green-100 text-green-800',
            'Pending L1': 'bg-yellow-100 text-yellow-800',
            'Pending L2': 'bg-yellow-100 text-yellow-800',
            'Ready for Allocation': 'bg-blue-100 text-blue-800',
            'Allocation Uploaded': 'bg-blue-100 text-blue-800'
        };

        const statusIcons = {
            'Active': '✅',
            'Listed': '🏛️',
            'Pending Review': '⏳',
            'Approved': '✅',
            'Rejected': '❌',
            'Draft': '📝',
            'Redemption in Process': '🔄',
            'Redemption Completed': '✅',
            'Burn in Process': '🔥',
            'Burn Completed': '⚫',
            'Matured': '🏁',
            'Allocation Finalized': '✅',
            'Pending L1': '⏳',
            'Pending L2': '⏳',
            'Ready for Allocation': '🔵',
            'Allocation Uploaded': '📤'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                <span className="mr-1">{statusIcons[status]}</span>
                {status}
            </span>
        );
    };

    // Filter bonds
    const EXCLUDED_STATUSES = new Set(['draft', 'pending l1', 'pending l2']);
    const visibleBonds = bonds.filter(bond => {
        const normalizedStatus = (bond.status || '').toLowerCase().replace(/_/g, ' ').trim();
        return !EXCLUDED_STATUSES.has(normalizedStatus);
    });

    const filteredBonds = visibleBonds.filter(bond => {
        const bondName = bond.bondName || '';
        const bondIsin = bond.isin || '';
        const bondType = bond.productType || '';
        const bondStatus = bond.status || '';

        return (
            bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bondIsin.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bondType.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bondStatus.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }).filter(bond => {
        return statusFilter === 'all' || bond.status === statusFilter;
    });

    // Summary widgets removed; no active/holder aggregation needed.

    if (bondsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holdings..." />;
    }

    if (bondsError) {
        console.error('Bonds error:', bondsError);
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="">
                <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Holdings</h1>
                            <p className="text-sm text-gray-500">Monitor issued bonds and holdings</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
                {/* Summary widgets removed per request */}

                {/* Filters and Search */}
                <div className="mt-8 card">
                    <div className="card-body">
                        <div className="flex flex-col gap-4 sm:flex-row">
                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                                    <input
                                        type="text"
                                        placeholder="Search by bond name, ISIN, or token symbol..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 form-input"
                                    />
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div className="w-full sm:w-48">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="form-input"
                                >
                                    <option value="all">All Status</option>
                                    <option value="Pending Review">Pending Review</option>
                                    <option value="Approved">Approved</option>
                                </select>
                            </div>

                            {/* Type Filter */}
                            <div className="w-full sm:w-48">
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="form-input"
                                >
                                    <option value="all">All Types</option>
                                    <option value="Fixed Notes">Conventional</option>
                                    <option value="Sukuk">Sukuk</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bond Holdings Table */}
                <div className="mt-8 card">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-secondary-900">
                                Bond Holdings ({totalBonds})
                            </h2>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-secondary-600">{searchTerm && `Filtered by "${searchTerm}"`}</span>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Bond Details</th>
                                    <th className="table-header-cell">Status</th>
                                    <th className="table-header-cell">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {filteredBonds.length > 0 ? (
                                    filteredBonds.map((bond) => {
                                        return (
                                            <tr key={bond._id} className="table-row">
                                                <td className="table-cell">
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="font-medium text-secondary-900">{bond.bondName}</div>
                                                            <span className="px-2 py-1 text-xs font-medium rounded bg-primary-100 text-primary-800">
                                                                {bond.tokenDetails?.symbol || '—'}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 text-sm text-secondary-500">
                                                            {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType} • {bond.isin || 'Pending ISIN'}
                                                        </div>
                                                        <div className="mt-1 text-xs text-dark-gray">
                                                            Issued: {formatDate(bond.createdAt)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div>
                                                        {getStatusBadge(bond.status)}
                                                        {bond.submittedAt && (
                                                            <div className="mt-1 text-xs text-secondary-400">
                                                                Submitted: {formatDate(bond.submittedAt)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => navigate(`/facility-agent/bond-holders/${bond._id}`)}
                                                            
                                                            className="p-1 text-primary-600 hover:text-primary-700"
                                                            title="View Bond Holders"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        {(() => {
                                                            const tokenAddress = bond?.tokenDetails?.tokenAddress || bond?.tokenDetails?.contractAddress;
                                                            const explorerBase = 'https://teleskop.aeris-dev.codedsolution-web3.com/address/';
                                                            if (!tokenAddress) {
                                                                return <span className="text-xs text-gray-400" title="Token not yet created">—</span>;
                                                            }
                                                            return (
                                                                <a
                                                                    href={`${explorerBase}${tokenAddress}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-1 text-primary-600 hover:text-primary-700"
                                                                    title="View on Explorer"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </a>
                                                            );
                                                        })()}
                                                        {bond.status === 'Active' && (
                                                            <button
                                                                onClick={() => navigate(`/facility-agent/coupon-profit-distribution?bondId=${bond._id}`)}
                                                                className="p-1 text-success-600 hover:text-success-700"
                                                                title="Manage Distribution"
                                                            >
                                                                <Coins className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="table-cell py-12 text-center">
                                            <div className="text-secondary-500">
                                                <Coins className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
                                                <p className="mb-2 text-lg font-medium">
                                                    {searchTerm ? 'No bonds found' : 'No bond holdings yet'}
                                                </p>
                                                <p className="mb-4 text-sm">
                                                    {searchTerm
                                                        ? `No bonds match your search "${searchTerm}"`
                                                        : 'No bonds are currently under management'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {filteredBonds.length > 0 && (
                            <div className="flex flex-col items-center justify-between gap-4 px-4 py-4 border-t sm:flex-row">
                                <div className="text-sm text-secondary-600">
                                    Showing page {currentPage} of {totalPages} ({totalBonds} bonds)
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >Prev</button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={`px-2 py-1 rounded border text-sm mx-0.5 ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}
                                            onClick={() => setCurrentPage(page)}
                                            disabled={page === currentPage}
                                        >{page}</button>
                                    ))}
                                    <button
                                        className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >Next</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FABondHoldings;
